import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Container, Table, Badge, Button, Modal, Form } from 'react-bootstrap';

const PageWrapper = styled.div`
  min-height: 100vh;
  padding: 120px 20px 80px;
  background-color: #0f0f13;
  color: #fff;
  font-family: "Pretendard", sans-serif;
`;

const ContentContainer = styled(Container)`
  max-width: 900px;
`;

const Title = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 30px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 20px;
`;

const StyledTable = styled(Table)`
  color: #fff;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  overflow: hidden;

  th, td {
    border-color: rgba(255, 255, 255, 0.1);
    vertical-align: middle;
    padding: 15px;
  }

  th {
    background: rgba(255, 255, 255, 0.1);
    font-weight: 600;
  }
`;

const RefundButton = styled(Button)`
  font-size: 0.85rem;
  padding: 5px 12px;
`;

const MyPage = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch('/api/my-payments', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Sort by date desc
        setPayments(data.reverse());
      }
    } catch (error) {
      console.error("Error fetching payments:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefundClick = (payment) => {
    setSelectedPayment(payment);
    setCancelReason('');
    setShowRefundModal(true);
  };

  const handleRefundSubmit = async () => {
    if (!cancelReason) {
      alert("취소 사유를 입력해주세요.");
      return;
    }

    setRefundLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/refund', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          paymentKey: selectedPayment.paymentKey,
          cancelReason: cancelReason
        })
      });

      const data = await response.json();

      if (response.ok) {
        alert("환불 처리가 완료되었습니다.");
        setShowRefundModal(false);
        fetchPayments(); // Refresh list
      } else {
        alert(data.detail || "환불 처리에 실패했습니다.");
      }
    } catch (error) {
      console.error("Refund error:", error);
      alert("서버 통신 중 오류가 발생했습니다.");
    } finally {
      setRefundLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const isRefundable = (approvedAt) => {
    if (!approvedAt) return false;
    const now = new Date();
    const approvedDate = new Date(approvedAt);
    const diffTime = Math.abs(now - approvedDate);
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    return diffDays <= 7;
  };

  return (
    <PageWrapper>
      <ContentContainer>
        <Title>결제 내역 / 환불</Title>
        
        {loading ? (
          <p>로딩 중...</p>
        ) : payments.length === 0 ? (
          <p style={{ color: '#aaa', textAlign: 'center', padding: '50px' }}>결제 내역이 없습니다.</p>
        ) : (
          <StyledTable responsive hover variant="dark">
            <thead>
              <tr>
                <th>결제일시</th>
                <th>상품명 (기간)</th>
                <th>금액</th>
                <th>상태</th>
                <th>관리</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p, idx) => {
                const refundable = isRefundable(p.approvedAt);
                return (
                  <tr key={idx}>
                    <td>{formatDate(p.approvedAt)}</td>
                    <td>{p.months}개월 구독권</td>
                    <td>{parseInt(p.amount).toLocaleString()}원</td>
                    <td>
                      {p.status === 'DONE' ? <Badge bg="success">결제완료</Badge> : 
                       p.status === 'CANCELED' ? <Badge bg="secondary">환불완료</Badge> : 
                       <Badge bg="warning">{p.status}</Badge>}
                    </td>
                    <td>
                      {p.status === 'DONE' && (
                        <RefundButton 
                          variant={refundable ? "outline-danger" : "outline-secondary"} 
                          onClick={() => refundable && handleRefundClick(p)}
                          disabled={!refundable}
                          title={refundable ? "" : "자동 환불 가능 기간(7일)이 지났습니다."}
                          style={{ 
                            cursor: refundable ? 'pointer' : 'not-allowed',
                            opacity: refundable ? 1 : 0.5
                          }}
                        >
                          환불 요청
                        </RefundButton>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </StyledTable>
        )}

        {/* Refund Modal */}
        <Modal show={showRefundModal} onHide={() => setShowRefundModal(false)} centered>
          <Modal.Header closeButton className="bg-dark text-white border-secondary">
            <Modal.Title>환불 요청</Modal.Title>
          </Modal.Header>
          <Modal.Body className="bg-dark text-white">
            <p>정말로 환불하시겠습니까?</p>
            <p className="text-white-50 small">
              환불 시 서비스 이용 기간이 차감되며, 규정에 따라 위약금이 발생할 수 있습니다.
              (현재 자동 환불 시스템은 전액 환불만 지원합니다. 부분 환불은 관리자에게 문의하세요.)
            </p>
            <Form.Group className="mt-3">
              <Form.Label>취소 사유</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3} 
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="취소 사유를 입력해주세요 (예: 단순 변심)"
                className="bg-secondary text-white border-secondary"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="bg-dark border-secondary">
            <Button variant="secondary" onClick={() => setShowRefundModal(false)}>
              닫기
            </Button>
            <Button 
              variant="danger" 
              onClick={handleRefundSubmit} 
              disabled={refundLoading || !cancelReason}
            >
              {refundLoading ? '처리 중...' : '환불하기'}
            </Button>
          </Modal.Footer>
        </Modal>

      </ContentContainer>
    </PageWrapper>
  );
};

export default MyPage;
